import {
  faBuilding,
  faCalculator,
  faListUl,
  faRuler,
  faSignature,
} from '@fortawesome/pro-regular-svg-icons'
import { SectionWrapper } from '../SectionWrapper/SectionWrapper'
import { DetailInfo, DetailRow } from '../DetailRow/DetailRow'
import { PlanType, Truncate, VerticalDivider } from '@labrav/react-components'
import { RenewalDetailsView } from '../RenewalDetailsView/RenewalDetailsView'
import { shouldShowRenewalDetailsFromTerms } from '../../utils/renewalDetailsFormat'
import {
  EntitlementOption,
  OfferTypeRenew,
  PrivateOffer,
} from '../../../modules/privateOffer/types'
import { useStyles } from './CloudBasedOfferInfo.styles'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import { DateTime } from 'luxon'
import { standardLabraDateFormat, useAccountId } from '@labrav/common'
import { useParams } from 'react-router-dom'
import { productDetailDate } from '../../utils/productDetailDate'
import { InstallmentsView } from '../InstallmentsView/InstallmentsView'
import {
  getCalculatedTcv,
  getUpliftedTcv,
  withInstallmentAmounts,
} from '../InstallmentsView/InstallmentsView.utils'
import { resolveSeedUpliftPercent } from '../../utils/renewalPricingTerms'
import { StandardCustomDimensions } from '../StandardCustomDimensions/StandardCustomDimensions'
import { AdditionalUsagePriceView } from '../AdditionalUsagePriceView/AdditionalUsagePriceView'
import { ExtendPrivateOfferExpiration } from '../ExtendPrivateOfferExpiration'
import { formatContractDurationBetweenDates } from '../../../utils/containerOfferHelperFunctions'
import { useFlagValue } from '@labrav/flags'

const RENEWAL_CHANNEL_LABELS: Record<string, string> = {
  EXTERNAL: 'Existing customer moving to AWS Marketplace',
  AWS_MARKETPLACE: 'Existing customer on AWS Marketplace',
}

export const getRenewalChannelLabel = (channel?: string | null) => {
  if (!channel) {
    return '-'
  }

  return RENEWAL_CHANNEL_LABELS[channel.toUpperCase()] ?? channel
}

export const AWSOfferInfo = ({
  privateOffer,
  planType,
  currencySymbol,
  partnerId,
}: {
  privateOffer: PrivateOffer
  planType?: PlanType
  currencySymbol?: string
  partnerId?: string
}) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()
  const { value: isRenewalPricingTermEnabled } =
    useFlagValue('renewalPricingTerm')
  const renewalInstallmentTcv = getUpliftedTcv(
    Number(privateOffer.tcv) ||
      getCalculatedTcv(privateOffer.installments || []),
    resolveSeedUpliftPercent(privateOffer)
  )
  const isProductSassSubscription = planType === PlanType.SAAS_SUBSCRIPTION
  const { accountId } = useParams<{
    cloud: string
    accountId: string
  }>()
  const fallbackAccountId = useAccountId({ product: 'flyout' }) || ''
  const routeAccountId = partnerId || accountId || fallbackAccountId || ''
  const isTypeRenewal = privateOffer.type === OfferTypeRenew.RENEWAL

  const offerExpirationDate = privateOffer.offerExpirationDate
    ? DateTime.fromISO(privateOffer.offerExpirationDate, {
        zone: timeZone,
      }).toFormat(standardLabraDateFormat)
    : '-'

  const { modifiedDate: createdAtDate, modifiedTime: createdAtTime } =
    privateOffer?.createdAt
      ? productDetailDate(privateOffer?.createdAt, timeZone)
      : { modifiedDate: '', modifiedTime: '' }

  const agreementStartDate = privateOffer.agreementStartDate
    ? DateTime.fromISO(privateOffer.agreementStartDate, {
        zone: timeZone,
      }).toFormat(standardLabraDateFormat)
    : '-'

  const hasAdditionalUsageDimension =
    privateOffer?.dimensions?.some(
      dimension =>
        dimension?.isAdditionalUsageAllowed ||
        dimension.type === 'additional_usage'
    ) || false

  const entitlementOptionMessage =
    privateOffer?.entitlementOption === EntitlementOption.SINGLE
      ? 'Buyer can choose only one unit of one of the existing dimensions.'
      : 'Buyer can choose one or more existing dimensions'

  const getOfferEndDateValue = (
    offer: PrivateOffer,
    timeZone?: string,
    planType?: PlanType
  ) => {
    const subscriptionEndDate =
      offer?.subscriptionEndDate ||
      (offer as PrivateOffer & { agreementEndDate?: string }).agreementEndDate
    if (subscriptionEndDate) {
      return DateTime.fromISO(subscriptionEndDate, {
        zone: timeZone,
      }).toFormat(standardLabraDateFormat)
    }
    if (planType === PlanType.SAAS_SUBSCRIPTION || isTypeRenewal) {
      return '-'
    }
    if (!offer?.agreementStartDate) {
      return '-'
    }

    return DateTime.fromISO(offer?.agreementStartDate, {
      zone: timeZone,
    })
      .plus({ months: offer?.serviceLength })
      .toFormat(standardLabraDateFormat)
  }

  const getContractDurationDisplay = (offer: PrivateOffer) => {
    if (offer?.serviceLength) {
      return `${offer.serviceLength} month${offer.serviceLength > 1 ? 's' : ''}`
    }
    const subscriptionEndDate =
      offer?.subscriptionEndDate ||
      (offer as PrivateOffer & { agreementEndDate?: string }).agreementEndDate
    if (subscriptionEndDate && offer?.agreementStartDate) {
      return formatContractDurationBetweenDates(
        offer.agreementStartDate,
        subscriptionEndDate
      )
    }
    return '-'
  }

  const renewal = privateOffer?.renewal ? 'Yes' : 'No'
  const renewalChannelLabel = privateOffer?.renewal
    ? getRenewalChannelLabel(privateOffer?.renewalChannel)
    : '-'
  const netPaymentTerms =
    privateOffer?.netPaymentTerms != null
      ? `${privateOffer?.netPaymentTerms} days`
      : '-'
  return (
    <>
      <SectionWrapper
        heading="Offer details"
        icon={faListUl}
        children={
          <div className={classes.section}>
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Offer name'}
                    value={privateOffer?.privateOfferName}
                    keyName={'privateOfferName'}
                  />
                  <DetailInfo
                    title={'Product name'}
                    value={
                      <Truncate width={'70%'} clipboardTooltip>
                        {privateOffer?.productName}
                      </Truncate>
                    }
                    keyName={'productName'}
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Created on'}
                    value={
                      <div className={classes.createdAtWrapper}>
                        <div className={classes.createdAtDate}>
                          {createdAtDate}
                        </div>
                        <div className={classes.createdAtTime}>
                          {createdAtTime}
                        </div>
                      </div>
                    }
                    keyName={'createdAtDate'}
                  />
                  <DetailInfo
                    title={'Offer expiration date'}
                    keyName="offerExpirationDate"
                    value={
                      <div className={classes.customWrapper}>
                        {offerExpirationDate}
                        {
                          <>
                            <VerticalDivider height="24px" />
                            {
                              <div data-testid="extend-expiration-date-2">
                                {' '}
                                <ExtendPrivateOfferExpiration
                                  offerExpirationDate={offerExpirationDate}
                                  offerId={privateOffer?.privateOfferId}
                                  planType={planType ?? PlanType.SAAS_CONTRACT}
                                  offerSubscriptionEndDate={
                                    privateOffer?.subscriptionEndDate || ''
                                  }
                                  offerStatus={privateOffer?.offerStatus}
                                  isDraft={privateOffer?.draft}
                                  aggrementStartDate={
                                    privateOffer?.agreementStartDate
                                  }
                                  type={privateOffer?.type}
                                  labraSubscriptionId={
                                    privateOffer?.labraSubscriptionId
                                  }
                                  subscription={privateOffer?.subscription}
                                  partnerId={routeAccountId}
                                  cloudMarketplace={'AWS'}
                                />
                              </div>
                            }
                          </>
                        }
                      </div>
                    }
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'AWS offer ID'}
                    keyName="awsOfferId"
                    value={privateOffer?.awsOfferId || ''}
                  />
                  <DetailInfo
                    title={'Labra offer ID'}
                    keyName="privateOfferId"
                    value={
                      <Truncate width={'60%'} clipboardTooltip>
                        {privateOffer?.privateOfferId}
                      </Truncate>
                    }
                  />
                </>
              }
            />
            <DetailRow
              borderBottom
              children={
                <>
                  <DetailInfo
                    title={'Renewal'}
                    keyName="renewal"
                    value={renewal}
                  />
                  <DetailInfo
                    title={'Renewal Type'}
                    keyName="renewalChannel"
                    value={renewalChannelLabel}
                  />
                </>
              }
            />
            <DetailRow
              borderBottom={false}
              children={
                <>
                  <DetailInfo
                    title={'Currency'}
                    keyName="currency"
                    value={privateOffer?.currency}
                  />
                  <DetailInfo
                    title={'Net payment terms'}
                    keyName="netPaymentTerms"
                    value={netPaymentTerms}
                  />
                </>
              }
            />
          </div>
        }
      />
      <SectionWrapper
        heading="Recipient company details"
        icon={faBuilding}
        children={
          <div className={classes.section}>
            <DetailRow
              borderBottom={false}
              children={
                <>
                  <DetailInfo
                    keyName="companyName"
                    title={'Company name'}
                    value={privateOffer?.companyName}
                  />
                  <DetailInfo
                    title={'AWS account number'}
                    keyName="awsAccountId"
                    value={privateOffer?.awsAccountId || ''}
                  />
                </>
              }
            />
          </div>
        }
      />
      <SectionWrapper
        heading="Contract details"
        icon={faSignature}
        children={
          <div className={classes.section} data-testid={'subscription-date'}>
            {!isProductSassSubscription && !isTypeRenewal && (
              <DetailRow
                borderBottom={false}
                className={classes.subscriptionRow}
                children={
                  <>
                    <DetailInfo
                      keyName="serviceLength"
                      title={'Contract duration'}
                      value={getContractDurationDisplay(privateOffer)}
                    />
                    <DetailInfo
                      title={'Contract start date'}
                      keyName="agreementStartDate"
                      value={getOfferStartDateValue(
                        privateOffer,
                        agreementStartDate
                      )}
                    />
                  </>
                }
              />
            )}
            <DetailRow
              borderBottom={false}
              className={classes.subscriptionEndDate}
              children={
                <>
                  <DetailInfo
                    keyName="subscriptionEndDate"
                    title={'Contract end date'}
                    value={getOfferEndDateValue(
                      privateOffer,
                      timeZone,
                      planType
                    )}
                  />
                </>
              }
            />
          </div>
        }
      />
      <SectionWrapper
        heading="Dimensions details"
        icon={faRuler}
        className={classes.dimensionBox}
        children={
          <div className={classes.dimensionWrapper}>
            <div className={classes.subTitle}>{entitlementOptionMessage}</div>
            <StandardCustomDimensions
              dimensions={privateOffer?.dimensions}
              flexiblePaymentTerms={privateOffer?.flexiblePaymentTerms}
              currencySymbol={currencySymbol}
            />
          </div>
        }
      />
      {privateOffer?.flexiblePaymentTerms && (
        <SectionWrapper
          heading={`Installments (${
            (privateOffer?.installments || []).length
          })`}
          icon={faRuler}
          children={
            <InstallmentsView
              installments={privateOffer?.installments}
              currencySymbol={currencySymbol}
            />
          }
        />
      )}
      {isRenewalPricingTermEnabled &&
        shouldShowRenewalDetailsFromTerms(privateOffer) && (
          <SectionWrapper
            heading="Renewal details"
            icon={faListUl}
            children={
              <div
                className={classes.section}
                data-testid="offer-renewal-details"
              >
                <RenewalDetailsView privateOffer={privateOffer} />
              </div>
            }
          />
        )}
      {isRenewalPricingTermEnabled &&
        planType === PlanType.SAAS_CONTRACT &&
        !!privateOffer.flexiblePaymentTerms &&
        (privateOffer.renewalInstallments?.length ?? 0) > 0 && (
          <SectionWrapper
            heading="Renewal installments"
            icon={faListUl}
            children={
              <InstallmentsView
                installments={withInstallmentAmounts(
                  privateOffer.renewalInstallments || [],
                  renewalInstallmentTcv
                )}
                currencySymbol={currencySymbol}
                hideTcv
                layout="renewalSchedule"
                contractStartDate={
                  privateOffer.agreementStartDate ||
                  privateOffer.renewalFirstInstallmentDate ||
                  privateOffer.installments?.[0]?.paymentDate
                }
              />
            }
          />
        )}
      {hasAdditionalUsageDimension && (
        <SectionWrapper
          heading="Price for additional usage"
          icon={faCalculator}
          children={
            <AdditionalUsagePriceView
              dimensions={privateOffer?.dimensions}
              currencySymbol={currencySymbol}
            />
          }
        />
      )}
    </>
  )
}

export const getOfferStartDateValue = (
  offer: PrivateOffer,
  agreementStartDate: string
) => {
  if (offer?.agreementStartDate) {
    return agreementStartDate
  }
  if (offer?.variableStartDate) {
    return 'Accepted date'
  }
  return '-'
}
